import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useWakeLock from "../../hooks/useWakeLock";
import { selectVerse } from "../../actions/sessionActions";
import { disableSoundSync, enableSoundSync } from "../../actions/soundSyncActions";
import { selectCurrentSong, selectHasSong, selectHostTranspose } from "../../selectors";
import { sortedVerses, verseLabel, firstLines } from "../../lib/lyrics";
import { transposeNote } from "../../lib/chords";
import { isLive, statusWord } from "./liveStatus";
import HostSheet from "./HostSheet";

/** The host's audio feature in the header: broadcasting the position by sound to nearby phones. */
const SoundChip = () => {
  const dispatch = useDispatch();
  const sync = useSelector((state) => state.soundSync);
  const on = sync.enabled && sync.role === "host";
  const label = on ? (sync.status === "interrupted" ? "Interrupted" : "Broadcasting") : sync.status === "starting" ? "Starting…" : "Sound";
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline"
      aria-pressed={on}
      aria-label="Sound sync"
      disabled={sync.status === "starting"}
      onClick={() => dispatch(on ? disableSoundSync() : enableSoundSync())}
    >
      <span aria-hidden="true">◉</span>
      <span>{label}</span>
    </button>
  );
};

/**
 * The host's live page: every section of the song is a tile, the one on screens is the
 * only thing coloured, and the sheet below holds the set.
 */
const HostLivePage = () => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const song = useSelector(selectCurrentSong);
  const hasSong = useSelector(selectHasSong);
  const hostTranspose = useSelector((state) => selectHostTranspose(state, song.Id));
  const keepAwake = useSelector((state) => state.preferences.keepAwake);
  const [sheetOpen, setSheetOpen] = useState(false);
  useWakeLock(keepAwake);

  const verses = sortedVerses(song);
  const live = isLive(session);
  const people = session.Listeners > 0 ? `${session.Listeners} singing` : statusWord(session);
  const key = song.Key ? ` · Key ${transposeNote(song.Key, hostTranspose)}` : "";

  return (
    <div className={`host-live${sheetOpen ? " sheet-open" : ""}`} data-screen-label="Live · host">
      <div className="live-head">
        <div className="live-head-text">
          <span className="live-title">{song.Title}</span>
          <span className="live-status" aria-live="polite">
            <span className={`dot${live ? "" : " dot-off"}`} aria-hidden="true" />
            <span>
              {live ? people : statusWord(session)} · {session.Code}
              {key}
            </span>
          </span>
        </div>
        <SoundChip />
      </div>

      {hasSong ? (
        <div className="tiles" role="group" aria-label="Sections">
          {verses.map((verse, index) => {
            const on = verse.Order === session.VerseNum;
            const label = verseLabel(verse, index);
            return (
              <button key={verse.Order} type="button" className={`tile${on ? " on" : ""}`} aria-pressed={on} onClick={() => dispatch(selectVerse(session.Code, verse.Order))}>
                <span className="tile-label">{on ? `${label} · live` : label}</span>
                <span className="tile-text">{firstLines(verse, 6).join(" ")}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="card-dashed">
          <div className="card-title">Nothing on screens yet</div>
          <div className="row-sub" style={{ whiteSpace: "normal" }}>
            Open the set below and tap a song, or add one.
          </div>
        </div>
      )}

      <HostSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
};

export default HostLivePage;
